import { type Kampanya } from "@/mocks/kampanyalar";

interface KampanyaDetayProps {
  kampanya: Kampanya;
  onDevamEt: () => void;
  onGeri: () => void;
}

export default function KampanyaDetay({ kampanya, onDevamEt, onGeri }: KampanyaDetayProps) {
  return (
    <section id="kampanya-detay" className="py-20 md:py-28 bg-[#F7F3EE]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Back */}
        <button
          onClick={onGeri}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#1A0F0A] text-sm font-medium mb-10 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line" />
          Kampanyalara Dön
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left - Visuals */}
          <div>
            <div className="rounded-2xl overflow-hidden mb-4 h-72 md:h-96 border border-[#EDE5D8]">
              <img
                src={kampanya.detayGorsel}
                alt={kampanya.baslik}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl overflow-hidden h-24 border border-[#EDE5D8]">
                <img src={kampanya.gorsel} alt={kampanya.baslik} className="w-full h-full object-cover object-top" />
              </div>
              <div className="rounded-xl overflow-hidden h-24 border border-[#EDE5D8]">
                <img src={kampanya.detayGorsel} alt={kampanya.baslik} className="w-full h-full object-cover object-top" />
              </div>
              <div className="rounded-xl overflow-hidden h-24 bg-[#1A0F0A] flex items-center justify-center cursor-pointer group relative border border-[#E8B86D]/20">
                <div className="w-10 h-10 rounded-full bg-[#E8B86D] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-play-fill text-[#1A0F0A] text-lg" />
                </div>
                <span className="absolute bottom-2 left-0 right-0 text-center text-[10px] text-white/50">Video İzle</span>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <span className="inline-block bg-[#1A0F0A] text-[#E8B86D] text-xs font-bold px-4 py-1.5 rounded-full tracking-[0.15em] uppercase mb-4">
              {kampanya.etiket}
            </span>

            <h2
              className="font-black italic text-[#1A0F0A] mb-5 leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              }}
            >
              {kampanya.baslik}
            </h2>

            <p className="text-[#5A4E45] text-base leading-relaxed mb-7 font-light">
              {kampanya.detayAciklama}
            </p>

            {/* Advantages */}
            <div className="mb-7">
              <h4 className="text-xs font-bold text-[#E8B86D] uppercase tracking-[0.2em] mb-4">
                Neler Dahil?
              </h4>
              <ul className="space-y-3">
                {kampanya.avantajlar.map((av) => (
                  <li key={av} className="flex items-center gap-3 text-[#1A0F0A] text-sm">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#E8B86D] text-[#1A0F0A] flex-shrink-0">
                      <i className="ri-check-line text-xs font-bold" />
                    </span>
                    {av}
                  </li>
                ))}
              </ul>
            </div>

            {/* Price */}
            <div className="flex items-end gap-3 mb-7 p-5 bg-[#1A0F0A] rounded-xl border border-[#E8B86D]/20">
              <div>
                <span className="text-white/35 text-sm line-through block">{kampanya.eskiFiyat}</span>
                <span
                  className="font-black italic"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "2rem",
                    background: "linear-gradient(135deg, #C9943A, #E8B86D)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {kampanya.yeniFiyat}
                </span>
              </div>
              <span className="text-white/45 text-sm mb-1">/ seans</span>
              <span className="ml-auto bg-[#E8B86D]/15 text-[#E8B86D] text-xs font-bold px-3 py-1 rounded-full border border-[#E8B86D]/25">
                Tasarruf Et
              </span>
            </div>

            {/* Customer Review */}
            <div className="bg-white rounded-xl p-5 mb-7 border border-[#EDE5D8]">
              <i className="ri-double-quotes-l text-2xl text-[#E8B86D] mb-2 block" />
              <p className="text-[#4A4A4A] text-sm italic leading-relaxed mb-4 font-light">
                {kampanya.musteriYorumu.yorum}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#E8B86D]/30">
                  <img src={kampanya.musteriYorumu.avatar} alt={kampanya.musteriYorumu.isim} className="w-full h-full object-cover" />
                </div>
                <div>
                  <span className="text-xs font-bold text-[#1A0F0A]">{kampanya.musteriYorumu.isim}</span>
                  <div className="flex gap-0.5 mt-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-[#E8B86D] text-xs" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={onDevamEt}
              className="w-full bg-[#1A0F0A] hover:bg-[#2D1810] text-white font-bold py-4 rounded-xl text-base transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border border-[#E8B86D]/20"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Devam Et
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}